import { ApplicationElement, html } from '@beunity/lit-components';
import translations from './translations.js';
import style from './style.lit.css';
import CommonActions from '@beunity/lit-components/components/common_actions/element.js';
import '@beunity/lit-components/components/timeago/element.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * Event card, also serves as a link to a more detailed view of event.
 *
 * @prop cardId - ID of the card.
 * @prop startsAt - Event starting date (or datetime).
 * @prop address - The address of the event.
 * @prop attendeesSrc - Array of the attending users to show as avatar list.
 * @prop attendeesLimit - Maximum number of attendees.
 * @prop attendantsCountTaken - Current number of attendees.
 * @prop hiddenReasonStatus - The reason why this card is hidden from other users (if present, will show "hidden because" notice) (options: 10, 20, 30, 40, 50, 60).
 * @prop editedBy - Name of the admin who edited the post (if present, will show "edited by" notice).
 * @prop editedDate - Date when admin edited the post.
 * @prop shareOn - Should add share button to card?
 * @prop pinValue - Is the card pinned? (If true, will show "pinned" notice).
 * @prop allDay - Is all day event?
 *
 * @slot gallery - For image.
 * @slot title - For title (Demo: MONDAY RISE & SHINE Yoga, Philosophy & Meditation).
 * @slot origin - For origin (Demo: (icon) + Yoga and Social Group in the Community).
 * @slot menu - For menu in the top-right corner.
 * @slot footer-left - For left-side content of the footer (Demo: Like and comment buttons).
 * @slot footer-right - For right-side content of the footer (Demo: Going/Not going button).
 *
 */

export default class CardEventLine1 extends ApplicationElement {
  /** @internal */
  static translations = translations;
  static styles = [ApplicationElement.styles, style];
  static properties = {
    cardId: { type: Number },
    startsAt: { type: Date },
    allDay: { type: Boolean },
    address: { type: String },
    attendeesSrc: { type: Array },
    attendeesLimit: { type: Number },
    attendantsCountTaken: { type: Number },
    hiddenReasonStatus: { type: Number },
    editedBy: { type: String },
    editedDate: { type: Date },
    shareOn: { type: Boolean },
    pinValue: { type: Boolean },
    name: { type: String, state: true },
  };
  static watchedProps = ['subscribeValue', 'bookmarkValue', 'pinValue'];

  constructor() {
    super();

    /** @internal */
    this.commonActions = new CommonActions(this);
    this.attendeesSrc = [];
  }

  connectedCallback() {
    super.connectedCallback();
  }

  /** @internal */
  get url() {
    return `/event_cards/${this.cardId}`;
  }

  /** @internal */
  get isAttendantsSlotFull() {
    return this.attendantsCountTaken >= this.attendeesLimit;
  }

  /** @internal */
  get hiddenReasonText() {
    return this.t(`hidden_reason.reason_${this.hiddenReasonStatus}`);
  }

  /** @internal */
  get dateFormat() {
    return `${this.allDay ? 'date' : 'time'}.formats.medium`;
  }

  async setShareTrigger() {
    await this.updateComplete;

    const shareBtn = this.shadowRoot.querySelector('ef-social-share button');

    if (!shareBtn) return;

    shareBtn.innerHTML = `
      <bu-icon name="share-nodes"></bu-icon>
    `;
    shareBtn.classList.add('bu-btn', 'btn-naked-dark');
  }

  onSlotChange(e) {
    this.name = e.target.assignedElements({ flatten: true })[0].innerText;
  }

  renderHiddenReasonMsg() {
    return html`
      <div class="notice t1">
        <bu-icon name="circle-exclamation:solid"></bu-icon>
        <span>
          <strong>${this.hiddenReasonText}</strong>
          ${unsafeHTML(this.t('hidden_reason_notice'))}
        </span>
      </div>
    `;
  }

  renderPinnedMsg() {
    return html`
      <div class="notice t2">
        <bu-icon name="thumbtack:solid"></bu-icon>
        <span>${this.t('pinned_msg')}</span>
      </div>
    `;
  }

  renderAttendantsSlotFullMsg() {
    return html`
      <div class="notice t3">
        <span>${this.t('event_full_msg')}</span>
      </div>
    `;
  }

  renderEditedMsg() {
    return html`
      <div class="notice t4">
        <bu-icon name="pen:solid"></bu-icon>
        <span>
          ${this.t('edited_by_msg', { subject: this.editedBy })}
          <ef-timeago .timestamp=${this.editedDate}></ef-timeago>
        </span>
      </div>
    `;
  }

  renderNotice() {
    return html`
      ${this.hiddenReasonStatus ? this.renderHiddenReasonMsg() : null}
      ${this.pinValue ? this.renderPinnedMsg() : null}
      ${this.editedBy ? this.renderEditedMsg() : null}
    `;
  }

  renderSocialShare() {
    return html`
      <ef-social-share
        title="${ifDefined(this.name)} / beUnity"
        url="${window.location.origin}${this.url}"
        dropdownMenu="true"
        btnClass="bu-btn btn-naked-dark">
      </ef-social-share>
    `;
  }

  renderParticipantsInfo() {
    return html`
      <span>
        ${this.attendeesLimit
          ? this.t('participants_of_total', {
              count: this.attendantsCountTaken,
              total: this.attendeesLimit,
            })
          : this.t('participants', { count: this.attendantsCountTaken })}
      </span>
    `;
  }

  handleEvent(e) {
    // remove active click on click outside
    if (e.type === 'click') this.handleMouseEnd();
  }

  handleMouseStart(e) {
    if (e.buttons == 1) {
      this.classList.add('active');
      this.mouseDown = true;
      document.addEventListener('click', this);
    }
  }

  handleMouseEnd() {
    if (this.mouseDown) {
      this.mouseDown = false;
      this.classList.remove('active');
      document.removeEventListener('click', this);
    }
  }

  render() {
    return html`
      ${this.renderNotice()}
      <article>
        <header>
          ${this.isAttendantsSlotFull
            ? this.renderAttendantsSlotFullMsg()
            : null}
          <slot name="gallery">
            <a href=${this.url} @mousedown=${this.handleMouseStart}>
              <ef-img-placeholder></ef-img-placeholder>
            </a>
          </slot>
        </header>
        <main>
          <a href=${this.url} @mousedown=${this.handleMouseStart}>
            <div class="info">
              ${this.i18n.l(this.dateFormat, this.startsAt)}
            </div>
            <slot name="title" @slotchange=${this.onSlotChange}></slot>
            <slot name="origin"></slot>
            <div class="participants">
              <ef-avatar-list
                class="avatar-list-sm "
                users=${JSON.stringify(this.attendeesSrc)}></ef-avatar-list>
              ${this.attendantsCountTaken > 0
                ? this.renderParticipantsInfo()
                : null}
            </div>
            <div class="address">
              <bu-icon name="location-dot:solid"></bu-icon>
              <span>${this.address}</span>
            </div>
          </a>
          <div class="quick-actions">
            ${this.shareOn && this.renderSocialShare()}
            <slot name="menu"></slot>
          </div>
        </main>
        <footer>
          <slot name="footer-left"></slot>
          <slot name="footer-right"></slot>
        </footer>
      </article>
    `;
  }
}

CardEventLine1.registerTagName('bu-card-event-line-1');

customElements.get('bu-card-event-line-1') ||
  customElements.define('bu-card-event-line-1', CardEventLine1);
